import React from 'react';
import { CheckCircle2, RefreshCw, Download, AlertTriangle, Rocket, Code2, X, Loader2, BookOpen, Megaphone } from 'lucide-react';

export type UpdateStatusPayload =
  | { type: 'checking' }
  | { type: 'up-to-date'; currentVersion: string }
  | { type: 'dev-mode'; currentVersion: string }
  | { type: 'error'; currentVersion?: string; message?: string }
  | { type: 'update-available'; version: string; currentVersion: string }
  | { type: 'update-downloaded' }
  | { type: 'sobre'; currentVersion: string; releaseDate: string; releaseNotes?: string };

interface DownloadProgress {
  percent: number;
  transferred: number;
  total: number;
  bytesPerSecond: number;
}

interface Props {
  payload: UpdateStatusPayload | null;
  onClose: () => void;
  onNavigateToAvisos?: () => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatSpeed(bps: number): string {
  if (bps < 1024 * 1024) return `${(bps / 1024).toFixed(0)} KB/s`;
  return `${(bps / (1024 * 1024)).toFixed(1)} MB/s`;
}

const UpdateStatusModal: React.FC<Props> = ({ payload, onClose, onNavigateToAvisos }) => {
  const [downloading, setDownloading] = React.useState(false);
  const [progress, setProgress] = React.useState<DownloadProgress | null>(null);

  // Subscribe to download progress events
  React.useEffect(() => {
    const electronAPI = (window as any).electronAPI;
    if (!electronAPI?.onDownloadProgress) return;
    const unsubscribe = electronAPI.onDownloadProgress((data: DownloadProgress) => {
      setProgress(data);
    });
    return unsubscribe;
  }, []);

  // Reset progress when payload changes away from update-available
  React.useEffect(() => {
    if (payload?.type !== 'update-available') {
      setDownloading(false);
      setProgress(null);
    }
  }, [payload?.type]);

  if (!payload) return null;

  const handleDownload = async () => {
    setDownloading(true);
    setProgress(null);
    await (window as any).electronAPI?.downloadUpdate?.();
    // From here: progress arrives via onDownloadProgress events,
    // completion/error arrives via onUpdateStatus which replaces the payload.
  };

  const handleInstall = async () => {
    await (window as any).electronAPI?.installUpdate?.();
  };

  const config: {
    icon: React.ReactNode;
    iconBg: string;
    title: string;
    description: React.ReactNode;
    primary?: { label: React.ReactNode; onClick: () => void; disabled?: boolean };
    secondary?: { label: string; onClick: () => void };
    wide?: boolean;
    body?: React.ReactNode;
  } = (() => {
    switch (payload.type) {
      case 'checking':
        return {
          icon: <Loader2 className="h-5 w-5 text-indigo-600 animate-spin" />,
          iconBg: 'bg-indigo-50',
          title: 'Verificando atualizações…',
          description: 'Conectando ao servidor de distribuição Panther.',
        };

      case 'up-to-date':
        return {
          icon: <CheckCircle2 className="h-5 w-5 text-green-600" />,
          iconBg: 'bg-green-50',
          title: 'Panther Access está atualizado',
          description: (
            <>
              Você está usando a versão mais recente.{' '}
              <span className="font-semibold text-gray-900">v{payload.currentVersion}</span>
            </>
          ),
          primary: { label: 'OK', onClick: onClose },
        };

      case 'dev-mode':
        return {
          icon: <Code2 className="h-5 w-5 text-violet-600" />,
          iconBg: 'bg-violet-50',
          title: 'Modo de Desenvolvimento',
          description: (
            <>
              A verificação de atualizações está desabilitada em modo de desenvolvimento.{' '}
              <span className="text-gray-500">Versão atual: v{payload.currentVersion}</span>
            </>
          ),
          primary: { label: 'OK', onClick: onClose },
        };

      case 'error':
        return {
          icon: <AlertTriangle className="h-5 w-5 text-red-600" />,
          iconBg: 'bg-red-50',
          title: 'Erro na atualização',
          description: (
            <span>
              {payload.message
                ? <span className="font-mono text-[10px] text-red-500 block mb-1 break-all">{payload.message}</span>
                : null}
              Verifique sua conexão com a internet e tente novamente.
            </span>
          ),
          primary: { label: 'Fechar', onClick: onClose },
        };

      case 'update-available':
        return {
          icon: downloading
            ? <Loader2 className="h-5 w-5 text-indigo-600 animate-spin" />
            : <Download className="h-5 w-5 text-indigo-600" />,
          iconBg: 'bg-indigo-50',
          title: downloading ? 'Baixando atualização…' : 'Nova versão disponível',
          description: downloading ? null : (
            <span>
              A versão <span className="font-semibold text-gray-900">v{payload.version}</span> está disponível.
              {' '}<span className="text-gray-400">Versão atual: v{payload.currentVersion}</span>
            </span>
          ),
          primary: downloading ? undefined : {
            label: (
              <span className="flex items-center gap-2">
                <Download className="h-3.5 w-3.5" />
                Baixar agora
              </span>
            ),
            onClick: handleDownload,
          },
          secondary: downloading ? undefined : { label: 'Mais tarde', onClick: onClose },
        };

      case 'update-downloaded':
        return {
          icon: <Rocket className="h-5 w-5 text-indigo-600" />,
          iconBg: 'bg-indigo-50',
          title: 'Pronto para atualizar',
          description: 'A nova versão foi baixada com sucesso. Reinicie o Panther Access para aplicar a atualização.',
          primary: {
            label: (
              <span className="flex items-center gap-2">
                <RefreshCw className="h-3.5 w-3.5" />
                Reiniciar agora
              </span>
            ),
            onClick: handleInstall,
          },
          secondary: { label: 'Depois', onClick: onClose },
        };

      case 'sobre':
        return {
          icon: <BookOpen className="h-5 w-5 text-indigo-600" />,
          iconBg: 'bg-indigo-50',
          title: 'Panther Access',
          description: (
            <span>
              <span className="font-mono font-semibold text-indigo-600">v{payload.currentVersion}</span>
              {payload.releaseDate && (
                <span className="block text-gray-400 mt-0.5">Lançamento: {payload.releaseDate}</span>
              )}
            </span>
          ),
          body: onNavigateToAvisos ? (
            <div className="pt-3 border-t border-gray-100">
              <button
                onClick={() => { onNavigateToAvisos(); onClose(); }}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900 transition-colors"
              >
                <Megaphone className="h-4 w-4 text-[#96bf13] flex-shrink-0" />
                <span>Ver avisos e atualizações</span>
              </button>
            </div>
          ) : undefined,
          primary: { label: 'Fechar', onClick: onClose },
        };
    }
  })();

  const isLoading = payload.type === 'checking';
  const isDownloading = payload.type === 'update-available' && downloading;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        onClick={isLoading || isDownloading ? undefined : onClose}
      />

      {/* Card */}
      <div className={`relative w-full bg-white rounded-2xl shadow-2xl ring-1 ring-black/5 overflow-hidden ${config.wide ? 'max-w-md' : 'max-w-sm'}`}>
        {/* Top accent bar — animates to progress width while downloading */}
        {isDownloading && progress ? (
          <div className="h-0.5 bg-gray-100 relative overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-indigo-500 to-violet-500 transition-all duration-300"
              style={{ width: `${progress.percent}%` }}
            />
          </div>
        ) : (
          <div className="h-0.5 bg-gradient-to-r from-indigo-500 via-violet-500 to-indigo-400" />
        )}

        {/* Header */}
        <div className="flex items-start gap-3 px-5 pt-5 pb-4">
          <div className={`flex-shrink-0 p-2.5 rounded-xl ${config.iconBg}`}>
            {config.icon}
          </div>
          <div className="flex-1 min-w-0 pt-0.5">
            <h3 className="text-sm font-semibold text-gray-900 leading-tight">{config.title}</h3>
            {config.description && (
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">{config.description}</p>
            )}

            {/* Download progress section */}
            {isDownloading && (
              <div className="mt-3 space-y-1.5">
                {/* Progress bar */}
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full transition-all duration-300"
                    style={{ width: `${progress?.percent ?? 0}%` }}
                  />
                </div>
                {/* Stats row */}
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-medium text-indigo-600">
                    {progress ? `${progress.percent}%` : '0%'}
                  </span>
                  <span className="text-[11px] text-gray-400">
                    {progress
                      ? `${formatBytes(progress.transferred)} / ${formatBytes(progress.total)} · ${formatSpeed(progress.bytesPerSecond)}`
                      : 'Iniciando…'}
                  </span>
                </div>
              </div>
            )}
          </div>
          {!isLoading && !isDownloading && (
            <button
              onClick={onClose}
              className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors -mt-0.5"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Release notes body */}
        {config.body && (
          <div className="px-5 pb-4 max-h-64 overflow-y-auto">
            {config.body}
          </div>
        )}

        {/* Footer */}
        {(config.primary || config.secondary) && (
          <div className="flex items-center justify-end gap-2 px-5 pb-5">
            {config.secondary && (
              <button
                onClick={config.secondary.onClick}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors"
              >
                {config.secondary.label}
              </button>
            )}
            {config.primary && (
              <button
                onClick={config.primary.onClick}
                disabled={config.primary.disabled}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors inline-flex items-center"
              >
                {config.primary.label}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateStatusModal;
